import type { ReactNode } from "react";
import { Skeleton } from "@/shared/components/ui/Skeleton";
import { cn } from "@/shared/lib/utils";

interface StatCardProps {
  label: string;
  value?: ReactNode;
  hint?: ReactNode;
  /**
   * When true, the card keeps its static label (and the card surface) but
   * shows a pulsing skeleton in place of the value while metrics load
   * (design.md §14). `value` is ignored in this state.
   */
  loading?: boolean;
  className?: string;
}

/**
 * A single metric tile for the admin dashboard. Glass-surface card with an
 * uppercase label, a prominent value, and an optional muted hint line.
 */
export function StatCard({ label, value, hint, loading, className }: StatCardProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-2xl glass-surface p-6",
        className,
      )}
    >
      <span className="type-label-sm tracking-wider uppercase text-text-muted">
        {label}
      </span>
      {loading ? (
        <Skeleton shape="text" className="h-7 w-20" />
      ) : (
        <span className="type-heading-md text-text">{value}</span>
      )}
      {hint ? <span className="type-body-sm text-text-muted">{hint}</span> : null}
    </div>
  );
}

/**
 * Human-readable process uptime, e.g. "3d 4h", "5h 12m", "8m", "45s".
 * Shows the two most significant non-zero units.
 */
export function formatUptime(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86_400);
  const hours = Math.floor((s % 86_400) / 3_600);
  const minutes = Math.floor((s % 3_600) / 60);
  const seconds = s % 60;

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (seconds && parts.length === 0) parts.push(`${seconds}s`);
  // When uptime is an exact number of minutes+ but no smaller unit shown.
  if (parts.length === 0) parts.push("0s");
  return parts.slice(0, 2).join(" ");
}
